import { DocumentStatus } from '@synergy/models';
import { tokens } from '@synergy/theme-palo';

/**
 * FRM-18: exactly one filled action, and it is the next step of the process.
 *
 * Every mask offered the same blue "Speichern", whatever state the document
 * was in — so on a saved order the step that actually moved it forward,
 * "Bestellung abschließen", stood white beside it and was missed, and a
 * completed invoice still offered to save changes it would not accept. The
 * decision which action is the filled one belongs to the state of the
 * document, so it is taken here once and read by every mask.
 */
export type PaloDocumentPhase = 'new' | 'draft' | 'done' | 'canceled';

/**
 * What the flow button of a mask does, which decides when it may be the filled
 * one: `advance` moves the document on (abschließen, senden, starten),
 * `followUp` makes the next document from it, `revoke` takes it back
 * (stornieren) and is never the main action — an action one cannot undo does
 * not belong under the cursor that was aiming for the next step.
 */
export type PaloFlowKind = 'advance' | 'followUp' | 'revoke';

const isDone = (status: DocumentStatus): boolean =>
    DocumentStatus.Completed === status ||
    DocumentStatus.CompletedWithTransition === status ||
    DocumentStatus.CompletedWithoutTransition === status;

/**
 * Where the document stands.
 *
 * `saved` is the document's id, not its status: a mask opened on /new shows a
 * status before anything exists on the server. A superadmin who unlocked the
 * document works on it again, so for him it reads as a draft.
 */
export const paloDocumentPhase = (
    status: DocumentStatus | undefined,
    options: { saved: boolean; unlocked?: boolean }
): PaloDocumentPhase => {
    if (!options.saved) {
        return 'new';
    }

    if (options.unlocked) {
        return 'draft';
    }

    if (DocumentStatus.Canceled === status) {
        return 'canceled';
    }

    return undefined !== status && isDone(status) ? 'done' : 'draft';
};

const grey = { color: tokens.color.ink2 };

/**
 * Narrow on purpose: the same values go onto a Button and onto the ButtonGroup
 * of a split button, and those two take different colour unions.
 */
export interface PaloActionAppearance {
    variant: 'contained' | 'outlined';
    color: 'primary' | 'error' | 'inherit';
    sx?: { color: string };
}

const filled: PaloActionAppearance = { variant: 'contained', color: 'primary' };

const plain = (color: 'error' | 'inherit'): PaloActionAppearance => ({
    variant: 'outlined',
    color,
    sx: 'inherit' === color ? grey : undefined,
});

/**
 * Why saving is off, or nothing when it is available.
 *
 * `comparesToServer` says whether "nothing changed" may be trusted — see
 * PaloSaveActionProps.
 */
export const paloSaveBlocked = (
    phase: PaloDocumentPhase,
    dirty: boolean,
    comparesToServer?: boolean
): 'completed' | 'canceled' | 'noChanges' | undefined => {
    // A completed document still takes what is the house's own — the internal
    // note and, where the mask leaves it open, the classification (FRM-18).
    // Everything else on it is locked, so a change can only be one of those.
    if ('done' === phase) {
        return dirty ? undefined : 'completed';
    }

    if ('canceled' === phase) {
        return 'canceled';
    }

    // A document that was never saved is saved even when nothing was typed —
    // its blank state is what the server does not have yet.
    return 'draft' === phase && !dirty && comparesToServer ? 'noChanges' : undefined;
};

/**
 * Which of a mask's actions is the filled one, given where the document stands.
 *
 * The cases are mutually exclusive by construction, which is what keeps it at
 * one filled button without any mask having to count them: saving while there
 * is something to save, advancing once there is not, and a follow-up only on a
 * document that is through. Taking it back (`revoke`) is never the main action.
 */
export const paloMainAction = (phase: PaloDocumentPhase, dirty: boolean): 'save' | 'advance' | 'followUp' | 'none' => {
    if ('canceled' === phase) {
        return 'none';
    }

    if ('done' === phase) {
        return dirty ? 'save' : 'followUp';
    }

    return 'new' === phase || dirty ? 'save' : 'advance';
};

/** The variant, colour and grey tint an action takes. */
export const paloActionAppearance = (
    phase: PaloDocumentPhase,
    dirty: boolean,
    target: PaloFlowKind | 'save'
): PaloActionAppearance => {
    if (paloMainAction(phase, dirty) === target) {
        return filled;
    }

    return plain('revoke' === target ? 'error' : 'inherit');
};
